import { useId, useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  getUserProfile,
  updateUserProfile,
  getCompanyDetails,
  updateCompanyDetails,
  getBusinessCardConfig,
  updateBusinessCardConfig,
} from '../api';

import { Button, SideDrawer } from '@/components';
import { env } from '@/common/config';
import { usePermissions } from '@/common/hooks';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import { PERMISSIONS } from '@/common/config/permissions';
import {
  DashboardPage,
  DashboardCard,
  DashboardInput,
} from '../components/PremiumDesign';
import { ImageCropModal } from '../components/ImageCropModal';
import user_icon from '../assets/user_icon.svg';
import facebook_profile from '../assets/Facebook_Profile.png';
import instagram_profile from '../assets/Instagram_Profile.png';
import threads_profile from '../assets/Threads_Profile.png';
import whatsapp_profile from '../assets/WhatsApp_Profile.png';
import edit_icon from '../assets/editIcon.png';
import profile_edit from '../assets/Profile_edit.png';
import { ProfileSettingsSkeleton } from '../components/ProfileSettingsSkeleton';
import {
  SOCIAL_PLATFORMS,
  classifySocialUrl,
  readSocialMap,
} from '@/common/utils/socialLink';

import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

export function ProfilePage() {
  const { can } = usePermissions();
  const canEditProfile = can(PERMISSIONS?.PROFILE?.EDIT || 'profile.edit');
  const canViewDigitalCard = can(
    PERMISSIONS?.DIGITAL_BUSINESS_CARD?.VIEW || 'business_card.view',
  );

  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    profileImage: null,
    previewUrl: null,
  });
  const fileInputRef = useRef(null);
  const profileFieldId = useId();

  const [companyForm, setCompanyForm] = useState({
    name: '',
    industry_type: '',
    company_size: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'IN',
    },
    tax_details: {
      tax_id: '',
      tax_type: 'gst',
      registration_number: '',
    },
  });

  const {
    data: profile,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['profile'],
    queryFn: getUserProfile,
  });

  const getCompanyId = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        return userObj?.company_id || localStorage.getItem('company_id');
      }
    } catch (e) {
      console.error('Failed to parse user object from localStorage', e);
    }
    return localStorage.getItem('company_id');
  };

  const companyId = profile?.company_id || getCompanyId();

  const {
    data: company,
    isLoading: isCompanyLoading,
    error: companyError,
    refetch: refetchCompany,
  } = useQuery({
    queryKey: ['company', companyId],
    queryFn: () => getCompanyDetails(companyId),
    enabled: !!companyId,
  });

  const { data: businessCardConfig, isLoading: isCardLoading } = useQuery({
    queryKey: ['business-card-config', companyId],
    queryFn: () => getBusinessCardConfig(companyId),
    enabled: !!companyId,
  });

  const socialMap = readSocialMap(profile, businessCardConfig);

  const showSkeleton = useDelayedSkeleton(
    isLoading ||
      (companyId && isCompanyLoading) ||
      (companyId && isCardLoading),
  );

  const updateProfileMutation = useMutation({
    mutationFn: updateUserProfile,
    onSuccess: (data) => {
      queryClient.setQueryData(['profile'], data);

      // Sync with local storage
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const userObj = JSON.parse(storedUser);
          userObj.first_name = data.first_name;
          userObj.last_name = data.last_name;
          if (data.email) userObj.email = data.email;
          if (data.phone) userObj.phone = data.phone;
          const imgUrl = data.profile_image || data.profile_picture;
          if (imgUrl) {
            userObj.profile_image = imgUrl;
            userObj.profile_picture = imgUrl;
          }
          localStorage.setItem('user', JSON.stringify(userObj));
        } else {
          localStorage.setItem(
            'user',
            JSON.stringify({
              first_name: data.first_name,
              last_name: data.last_name,
              email: data.email,
              phone: data.phone,
            }),
          );
        }
      } catch (e) {
        console.error('Failed to sync updated profile to localStorage', e);
      }

      // Notify other components (like Header)
      window.dispatchEvent(new Event('user-profile-updated'));

      toast.success('Profile updated successfully!');
      // Clean up object URL to avoid memory leaks
      if (editForm.previewUrl) URL.revokeObjectURL(editForm.previewUrl);
      setEditForm((prev) => ({
        ...prev,
        profileImage: null,
        previewUrl: null,
      }));
      setIsEditing(false);
    },
    onError: (err) => {
      const errorMsg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to update profile';
      toast.error(errorMsg);
    },
  });

  const updateCompanyMutation = useMutation({
    mutationFn: updateCompanyDetails,
    onSuccess: (data) => {
      queryClient.setQueryData(['company', companyId], data);
      queryClient.setQueryData(['profile'], (oldProfile) => {
        if (!oldProfile) return oldProfile;
        return {
          ...oldProfile,
          company_name: data.name,
          company_industry: data.industry_type,
          company_size: data.company_size,
          company_address: data.address,
        };
      });

      try {
        localStorage.setItem('company_name', data.name);
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const userObj = JSON.parse(storedUser);
          userObj.company_name = data.name;
          localStorage.setItem('user', JSON.stringify(userObj));
        }
      } catch (e) {
        console.error('Failed to sync updated company name to localStorage', e);
      }

      toast.success('Company details updated successfully!');
    },
    onError: (err) => {
      const errorMsg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to update company details';
      toast.error(errorMsg);
    },
  });

  const updateBusinessCardConfigMutation = useMutation({
    mutationFn: updateBusinessCardConfig,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['business-card-config', companyId], {
        ...data,
        ...variables?.payload,
      });
      if (!variables?.payload?.social_links) {
        toast.success('Digital business card updated successfully!');
      }
    },
    onError: (err) => {
      const errorMsg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to update digital business card';
      toast.error(errorMsg);
    },
  });

  const handleToggleBusinessCard = () => {
    if (!businessCardConfig) return;
    const newStatus = !businessCardConfig.is_active;
    updateBusinessCardConfigMutation.mutate({
      companyId,
      payload: { is_active: newStatus },
    });
  };

  const handleCopyBusinessCardLink = () => {
    if (!businessCardConfig?.short_id) return;
    const link = `${window.location.origin}/${businessCardConfig.short_id}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard!');
  };

  const handleOpenBusinessCard = () => {
    if (!businessCardConfig?.short_id) return;
    const link = `${window.location.origin}/${businessCardConfig.short_id}`;
    window.open(link, '_blank');
  };

  const fillEditForms = () => {
    setEditForm({
      firstName: profile?.first_name || '',
      lastName: profile?.last_name || '',
      email: profile?.email || '',
      phone: profile?.phone || '',
      profileImage: null,
      previewUrl: null,
    });

    const rawAddr = company?.address || profile?.company_address;
    setCompanyForm({
      name: company?.name || profile?.company_name || '',
      industry_type: company?.industry_type || profile?.company_industry || '',
      company_size: company?.company_size || profile?.company_size || '',
      address: {
        line1: rawAddr?.line1 || '',
        line2: rawAddr?.line2 || '',
        city: rawAddr?.city || '',
        state: rawAddr?.state || '',
        postal_code: rawAddr?.postal_code || '',
        country: rawAddr?.country || 'IN',
      },
      tax_details: {
        tax_id: company?.tax_details?.tax_id || '',
        tax_type: company?.tax_details?.tax_type || 'gst',
        registration_number: company?.tax_details?.registration_number || '',
      },
    });
  };

  const handleEditClick = () => {
    fillEditForms();
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    // Revoke any object URL to avoid memory leaks
    if (editForm.previewUrl) URL.revokeObjectURL(editForm.previewUrl);
    setIsEditing(false);
  };

  const [selectedCropImage, setSelectedCropImage] = useState(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);

  const handleProfileImageChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size must be less than 10MB');
      return;
    }
    const rawUrl = URL.createObjectURL(file);
    setSelectedCropImage(rawUrl);
    setIsCropModalOpen(true);
  };

  const handleCropComplete = (croppedFile, croppedPreviewUrl) => {
    if (editForm.previewUrl) URL.revokeObjectURL(editForm.previewUrl);
    setEditForm((prev) => ({
      ...prev,
      profileImage: croppedFile,
      previewUrl: croppedPreviewUrl,
    }));

    if (!isEditing) {
      updateProfileMutation.mutate(
        { profileImage: croppedFile },
        {
          onError: () => {
            URL.revokeObjectURL(croppedPreviewUrl);
            setEditForm((prev) => ({
              ...prev,
              profileImage: null,
              previewUrl: null,
            }));
          },
        },
      );
    }

    if (selectedCropImage) {
      URL.revokeObjectURL(selectedCropImage);
      setSelectedCropImage(null);
    }
  };

  const handleCloseCropModal = () => {
    if (selectedCropImage) {
      URL.revokeObjectURL(selectedCropImage);
      setSelectedCropImage(null);
    }
    setIsCropModalOpen(false);
  };

  const handleSaveClick = () => {
    if (!editForm.firstName.trim()) {
      toast.error('First Name cannot be empty');
      return;
    }
    if (!editForm.lastName.trim()) {
      toast.error('Last Name cannot be empty');
      return;
    }
    if (editForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!companyForm.name.trim()) {
      toast.error('Company Name cannot be empty');
      return;
    }
    if (!companyForm.address.line1.trim()) {
      toast.error('Address Line 1 cannot be empty');
      return;
    }
    if (!companyForm.address.city.trim()) {
      toast.error('City cannot be empty');
      return;
    }
    if (!companyForm.address.state.trim()) {
      toast.error('State cannot be empty');
      return;
    }
    if (!companyForm.address.postal_code.trim()) {
      toast.error('Postal Code cannot be empty');
      return;
    }

    updateProfileMutation.mutate(editForm);
    updateCompanyMutation.mutate({
      companyId,
      payload: companyForm,
    });
  };

  const rawAddr = company?.address || profile?.company_address;
  const addressValues = (() => {
    if (!rawAddr) {
      return {
        line1: '',
        line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'IN',
      };
    }
    const rawCity = rawAddr.city || '';
    const rawState = rawAddr.state || '';
    const rawPostal = rawAddr.postal_code || '';

    let city = rawCity;
    let state = rawState;
    let postal_code = rawPostal;

    // Detect swapped values: if city matches pincode digits (like "394326" or "395006")
    // or state is "Surat" (city) or postal code is "Gujarat" (state)
    if (
      (/^\d+$/.test(rawCity) || /^\d+$/.test(rawState)) &&
      (rawState.toLowerCase() === 'surat' ||
        rawPostal.toLowerCase() === 'gujarat')
    ) {
      city = rawState; // "Surat"
      state = rawPostal; // "Gujarat"
      postal_code = rawCity; // "394326"
    }

    return {
      line1: rawAddr.line1 || '',
      line2: rawAddr.line2 || '',
      city,
      state,
      postal_code,
      country: rawAddr.country || 'IN',
    };
  })();

  if (showSkeleton || !profile || (companyId && !company)) {
    return <ProfileSettingsSkeleton />;
  }

  if (error || (companyId && companyError)) {
    return (
      <DashboardPage className="!p-0 !bg-transparent">
        <div className="w-full flex flex-col items-center justify-center min-h-[400px] gap-4 font-sf-pro-display text-center">
          <div className="w-16 h-16 rounded-pill bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-500">
            <svg
              className="w-8 h-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-h6 font-bold text-text-primary">
              Failed to Load Profile
            </h3>
            <p className="text-body-sm text-text-secondary mt-1 max-w-xs mx-auto">
              {error?.response?.data?.detail ||
                companyError?.response?.data?.detail ||
                error?.message ||
                companyError?.message ||
                'There was an error connecting to the profile service.'}
            </p>
          </div>
          <Button
            variant="primary"
            onClick={() => {
              refetch();
              if (companyId) refetchCompany();
            }}
            className="mt-2 h-[48px] px-5"
          >
            Retry Loading
          </Button>
        </div>
      </DashboardPage>
    );
  }

  const user = {
    firstName: profile?.first_name || '',
    lastName: profile?.last_name || '',
    email: profile?.email || '',
    phone: profile?.phone || '',
    profile_image: profile?.profile_image || profile?.profile_picture || null,
    bio: 'Administrator',
    emailVerified: profile?.email_verified || false,
    phoneVerified: profile?.phone_verified || false,
  };

  const cityState = (() => {
    if (!rawAddr) return 'No Address Provided';
    return [addressValues.city, addressValues.state, addressValues.country]
      .filter(Boolean)
      .join(', ');
  })();

  return (
    <DashboardPage className="!p-0 !bg-transparent">
      <div className="flex flex-col gap-6 animate-fadeIn w-full pb-6">
        <div className="flex flex-col lg:flex-row gap-6 items-start">
          {/* LEFT SIDE — PROFILE SUMMARY CARD */}
          <DashboardCard className="w-full lg:w-1/3 h-auto lg:h-[470px] flex flex-col items-center justify-center p-6 lg:p-8 relative bg-white border border-border-default shadow-sm text-center min-w-0">
            <div className="relative mb-4 lg:mb-6 inline-block">
              <div className="w-32 h-32 lg:w-48 lg:h-48 rounded-full p-1 border-[3px] border-border-active flex items-center justify-center bg-white shadow-sm overflow-hidden">
                <img
                  src={
                    editForm.previewUrl
                      ? editForm.previewUrl
                      : user.profile_image
                        ? user.profile_image.startsWith('http')
                          ? user.profile_image
                          : `${env.photoBaseUrl}${user.profile_image}`
                        : user_icon
                  }
                  alt="Profile Avatar"
                  className="w-full h-full rounded-full object-cover"
                />
              </div>

              {canEditProfile && (
                <>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                    disabled={updateProfileMutation.isPending}
                    className="absolute bottom-0 right-0 lg:bottom-1 lg:right-2 w-8 h-8 lg:w-10 lg:h-10 rounded-full overflow-hidden hover:scale-105 transition-transform shadow-sm focus:outline-none"
                    title="Change profile photo"
                  >
                    <img
                      src={profile_edit}
                      alt="Change profile photo"
                      className="w-full h-full object-cover"
                    />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleProfileImageChange}
                  />
                </>
              )}
            </div>

            <h2 className="text-text-primary text-center font-['SF_Pro_Display'] font-medium text-2xl lg:text-[34px] leading-[100%] tracking-[0%] mt-2 lg:mt-0">
              {company?.name || profile?.company_name || 'Not Provided'}
            </h2>
            <p className="text-center font-['SF_Pro_Display'] font-normal text-base lg:text-[20px] leading-[100%] tracking-[0%] text-text-muted mt-2">
              {cityState}
            </p>
          </DashboardCard>

          {/* RIGHT SIDE — INFORMATION CARD */}
          <DashboardCard
            className="w-full lg:w-2/3 h-auto lg:h-[470px] flex flex-col overflow-y-auto bg-white border border-border-default shadow-sm min-w-0"
            noPadding
          >
            {/* Personal Information */}
            <div className="p-4 lg:p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xl font-medium text-primary">
                  Personal Information
                </h3>
                {canEditProfile && (
                  <button
                    type="button"
                    onClick={handleEditClick}
                    className="hover:opacity-80 transition-opacity p-1 rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    title="Edit Profile"
                    aria-label="Edit Profile"
                  >
                    <img
                      src={edit_icon}
                      alt="Edit"
                      className="w-5 h-5 object-contain"
                    />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-8">
                <div>
                  <span className="text-sm text-text-muted block mb-1">
                    First Name
                  </span>
                  <p className="text-[15px] font-medium text-black">
                    {user.firstName || '—'}
                  </p>
                </div>

                <div>
                  <span className="text-sm text-text-muted block mb-1">
                    Last Name
                  </span>
                  <p className="text-[15px] font-medium text-black">
                    {user.lastName || '—'}
                  </p>
                </div>

                <div>
                  <span className="text-sm text-text-muted block mb-1">
                    Email Address
                  </span>
                  <div className="flex items-center gap-1.5">
                    <p className="text-[15px] font-medium text-black break-all">
                      {user.email || '—'}
                    </p>
                    {user.emailVerified && (
                      <svg
                        className="w-[16px] h-[16px] text-primary"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    )}
                  </div>
                </div>

                <div>
                  <span className="text-sm text-text-muted block mb-1">
                    Social Links
                  </span>
                  <div className="flex gap-2 mt-1.5">
                    {SOCIAL_PLATFORMS.map((platform) => {
                      const checked = classifySocialUrl(
                        platform.key,
                        socialMap[platform.key],
                      );
                      const iconSrc = {
                        whatsapp: whatsapp_profile,
                        instagram: instagram_profile,
                        facebook: facebook_profile,
                        threads: threads_profile,
                      }[platform.key];
                      if (checked.status === 'invalid') {
                        return (
                          <button
                            key={platform.key}
                            type="button"
                            disabled
                            aria-label={`${platform.label} link unavailable`}
                            title={`${platform.label} link unavailable`}
                            className="flex items-center justify-center w-[24px] h-[24px] opacity-40 cursor-not-allowed"
                          >
                            <img
                              src={iconSrc}
                              alt=""
                              className="w-full h-full object-contain grayscale"
                            />
                          </button>
                        );
                      }
                      if (checked.status !== 'valid') return null;
                      return (
                        <a
                          key={platform.key}
                          href={checked.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Open ${platform.label}`}
                          title={`Open ${platform.label}`}
                          className="flex items-center justify-center w-[24px] h-[24px] hover:opacity-80 transition-opacity rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                        >
                          <img
                            src={iconSrc}
                            alt=""
                            className="w-full h-full object-contain"
                          />
                        </a>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <hr className="border-border-default border-t m-0" />

            {/* Company Information */}
            <div className="p-4 lg:p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xl font-medium text-primary">
                  Company Information
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-8">
                <div>
                  <span className="text-base text-text-muted block mb-1">
                    Company Name
                  </span>
                  <p className="text-[15px] font-medium text-black">
                    {company?.name || profile?.company_name || '—'}
                  </p>
                </div>

                <div>
                  <span className="text-base text-text-muted block mb-1">
                    Address
                  </span>
                  <p className="text-[15px] font-medium text-black break-words min-w-0">
                    {addressValues.line1 || '—'}
                  </p>
                </div>

                <div>
                  <span className="text-base text-text-muted block mb-1">
                    City
                  </span>
                  <p className="text-[15px] font-medium text-black">
                    {addressValues.city || '—'}
                  </p>
                </div>

                <div>
                  <span className="text-base text-text-muted block mb-1">
                    State
                  </span>
                  <p className="text-[15px] font-medium text-black">
                    {addressValues.state || '—'}
                  </p>
                </div>

                <div>
                  <span className="text-base text-text-muted block mb-1">
                    Country Code (2-Letter)
                  </span>
                  <p className="text-[15px] font-medium text-black">
                    {addressValues.country || '—'}
                  </p>
                </div>

                <div>
                  <span className="text-base text-text-muted block mb-1">
                    Postal Code
                  </span>
                  <p className="text-[15px] font-medium text-black">
                    {addressValues.postal_code || '—'}
                  </p>
                </div>
              </div>
            </div>
          </DashboardCard>
        </div>

        {/* DIGITAL BUSINESS CARD SECTION */}
        {businessCardConfig && canViewDigitalCard && (
          <DashboardCard
            className="flex flex-col bg-white border border-border-default shadow-sm"
            elevated={false}
          >
            {/* Header */}
            <div className="flex flex-row items-start justify-between gap-4 mb-4">
              <div className="flex-1">
                <h3 className="text-lg text-primary leading-tight mb-1">
                  Digital Business Card
                </h3>
                <p className="text-sm text-text-muted leading-snug">
                  Manage your public business card and shareable link.
                </p>
              </div>

              {canEditProfile && (
                <div className="flex items-center shrink-0 pt-1">
                  <button
                    type="button"
                    onClick={handleToggleBusinessCard}
                    disabled={updateBusinessCardConfigMutation.isPending}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${businessCardConfig.is_active ? 'bg-primary' : 'bg-gray-300'}`}
                    role="switch"
                    aria-checked={businessCardConfig.is_active}
                    aria-label="Public business card"
                  >
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${businessCardConfig.is_active ? 'translate-x-5' : 'translate-x-0'}`}
                    />
                  </button>
                </div>
              )}
            </div>

            {/* Business Card Row */}
            <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between gap-4 bg-[#EDF5EE] rounded-lg p-3 lg:p-4 w-full">
              {/* Profile Info */}
              <div className="flex items-center gap-3 w-full xl:w-auto min-w-0">
                <div className="w-12 h-12 rounded-full border border-primary p-0.5 flex items-center justify-center shrink-0 bg-white">
                  <img
                    src={
                      user.profile_image
                        ? user.profile_image.startsWith('http')
                          ? user.profile_image
                          : `${env.photoBaseUrl}${user.profile_image}`
                        : user_icon
                    }
                    alt="Company Logo"
                    className="w-full h-full rounded-full object-cover"
                  />
                </div>
                <div className="flex flex-col flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                    <h4 className="font-medium text-black text-base truncate">
                      {company?.name || profile?.company_name || 'Not Provided'}
                    </h4>
                    {businessCardConfig.is_active ? (
                      <div className="px-2 py-0.5 rounded-md border text-[10px] font-medium flex items-center gap-1 shrink-0 border-primary text-primary bg-white">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                        Public
                      </div>
                    ) : (
                      <div className="px-2 py-0.5 rounded-md border text-[10px] font-medium flex items-center gap-1 shrink-0 border-red-500 text-red-500 bg-white">
                        <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>
                        Inactive
                      </div>
                    )}
                  </div>
                  <p className="text-text-muted text-sm truncate">
                    {(
                      user.firstName.trim() +
                      ' ' +
                      user.lastName.trim()
                    ).trim() ||
                      profile?.username ||
                      user.email ||
                      'User'}
                  </p>
                </div>
              </div>

              {/* Actions */}
              {businessCardConfig.is_active && (
                <div className="flex flex-col sm:flex-row items-center gap-3 w-full xl:w-auto shrink-0">
                  <div className="flex items-center border rounded-md bg-white px-3 h-[36px] min-w-0 w-full sm:w-[280px] border-primary">
                    <span className="text-sm text-text-muted truncate flex-1 select-all">
                      {`${window.location.origin}/${businessCardConfig.short_id}`}
                    </span>
                    <button
                      onClick={handleCopyBusinessCardLink}
                      className="ml-2 shrink-0 transition-opacity flex items-center justify-center hover:opacity-70 text-primary"
                      type="button"
                      aria-label="Copy link"
                      title="Copy Link"
                    >
                      <svg
                        aria-hidden="true"
                        className="w-[16px] h-[16px]"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <rect
                          x="9"
                          y="9"
                          width="11"
                          height="11"
                          rx="2"
                          ry="2"
                        />
                        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                      </svg>
                    </button>
                  </div>

                  <button
                    onClick={handleOpenBusinessCard}
                    className="h-[36px] px-4 rounded-md text-white flex items-center gap-2 whitespace-nowrap w-full sm:w-auto justify-center text-sm font-medium transition-colors bg-primary hover:bg-primary/80 shrink-0"
                  >
                    <span>Open Card</span>
                    <svg
                      className="w-[16px] h-[16px]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M7 17L17 7M17 7H9M17 7V15"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </DashboardCard>
        )}

        {/* Edit Profile Modal */}
        <SideDrawer
          isOpen={isEditing}
          onClose={handleCancelClick}
          width="lg"
          labelledBy="edit-profile-dialog-title"
          header={
            <div className="flex items-center justify-between w-full">
              <h2
                id="edit-profile-dialog-title"
                className="text-xl font-bold text-black"
              >
                Edit Profile
              </h2>
              <button
                type="button"
                aria-label="Close dialog"
                onClick={handleCancelClick}
                disabled={
                  updateProfileMutation.isPending ||
                  updateCompanyMutation.isPending
                }
                className="w-7 h-7 rounded bg-primary text-white flex items-center justify-center hover:bg-primary/80 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                <svg
                  aria-hidden="true"
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          }
          footer={
            <div className="flex justify-end w-full">
              <Button
                variant="primary"
                onClick={handleSaveClick}
                disabled={
                  updateProfileMutation.isPending ||
                  updateCompanyMutation.isPending
                }
                className="px-5 py-2.5 h-[40px] text-[14px] font-medium rounded-lg gap-2 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#217A1C]"
              >
                {updateProfileMutation.isPending ||
                updateCompanyMutation.isPending ? (
                  'Saving...'
                ) : (
                  <>
                    Save
                    <svg
                      className="w-4 h-4 ml-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </>
                )}
              </Button>
            </div>
          }
        >
          <div className="flex flex-col gap-8">
            {/* Personal Information */}
            <div className="flex flex-col gap-4">
              <h3 className="text-lg font-medium text-primary">
                Personal Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor={`${profileFieldId}-first-name`}
                    className="text-sm text-text-muted block mb-1"
                  >
                    First Name
                  </label>
                  <DashboardInput
                    id={`${profileFieldId}-first-name`}
                    type="text"
                    value={editForm.firstName}
                    onChange={(e) =>
                      setEditForm({ ...editForm, firstName: e.target.value })
                    }
                    className="w-full bg-gray-50/50"
                    disabled={updateProfileMutation.isPending}
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <label
                    htmlFor={`${profileFieldId}-last-name`}
                    className="text-sm text-text-muted block mb-1"
                  >
                    Last Name
                  </label>
                  <DashboardInput
                    id={`${profileFieldId}-last-name`}
                    type="text"
                    value={editForm.lastName}
                    onChange={(e) =>
                      setEditForm({ ...editForm, lastName: e.target.value })
                    }
                    className="w-full bg-gray-50/50"
                    disabled={updateProfileMutation.isPending}
                    placeholder="Enter last name"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor={`${profileFieldId}-email`}
                    className="text-sm text-text-muted block mb-1"
                  >
                    Email Address
                  </label>
                  <DashboardInput
                    id={`${profileFieldId}-email`}
                    type="email"
                    value={editForm.email}
                    readOnly
                    className="w-full !bg-gray-200/90 !text-gray-500 !border-gray-300 cursor-default focus:ring-0"
                    placeholder="Enter email address"
                  />
                </div>
              </div>
            </div>

            {/* Company Information */}
            <div className="flex flex-col gap-4">
              <h3 className="text-lg font-medium text-primary">
                Company Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label
                    htmlFor={`${profileFieldId}-company-name`}
                    className="text-sm text-text-muted block mb-1"
                  >
                    Company Name
                  </label>
                  <DashboardInput
                    id={`${profileFieldId}-company-name`}
                    type="text"
                    value={companyForm.name}
                    onChange={(e) =>
                      setCompanyForm({ ...companyForm, name: e.target.value })
                    }
                    className="w-full bg-gray-50/50"
                    disabled={updateCompanyMutation.isPending}
                    placeholder="Enter company name"
                  />
                </div>
                <div className="md:col-span-2">
                  <label
                    htmlFor={`${profileFieldId}-address`}
                    className="text-sm text-text-muted block mb-1"
                  >
                    Address Line 1
                  </label>
                  <DashboardInput
                    id={`${profileFieldId}-address`}
                    type="text"
                    value={companyForm.address.line1}
                    onChange={(e) =>
                      setCompanyForm({
                        ...companyForm,
                        address: {
                          ...companyForm.address,
                          line1: e.target.value,
                          line2: '',
                        },
                      })
                    }
                    className="w-full bg-gray-50/50"
                    disabled={updateCompanyMutation.isPending}
                    placeholder="Enter address"
                  />
                </div>
                <div>
                  <label
                    htmlFor={`${profileFieldId}-city`}
                    className="text-sm text-text-muted block mb-1"
                  >
                    City Name
                  </label>
                  <DashboardInput
                    id={`${profileFieldId}-city`}
                    type="text"
                    value={companyForm.address.city}
                    onChange={(e) =>
                      setCompanyForm({
                        ...companyForm,
                        address: {
                          ...companyForm.address,
                          city: e.target.value,
                        },
                      })
                    }
                    className="w-full bg-gray-50/50"
                    disabled={updateCompanyMutation.isPending}
                    placeholder="Enter City name"
                  />
                </div>
                <div>
                  <label
                    htmlFor={`${profileFieldId}-state`}
                    className="text-sm text-text-muted block mb-1"
                  >
                    State Name
                  </label>
                  <DashboardInput
                    id={`${profileFieldId}-state`}
                    type="text"
                    value={companyForm.address.state}
                    onChange={(e) =>
                      setCompanyForm({
                        ...companyForm,
                        address: {
                          ...companyForm.address,
                          state: e.target.value,
                        },
                      })
                    }
                    className="w-full bg-gray-50/50"
                    disabled={updateCompanyMutation.isPending}
                    placeholder="Enter State name"
                  />
                </div>
                <div>
                  <label
                    htmlFor={`${profileFieldId}-postal`}
                    className="text-sm text-text-muted block mb-1"
                  >
                    Postal Code
                  </label>
                  <DashboardInput
                    id={`${profileFieldId}-postal`}
                    type="text"
                    value={companyForm.address.postal_code}
                    onChange={(e) =>
                      setCompanyForm({
                        ...companyForm,
                        address: {
                          ...companyForm.address,
                          postal_code: e.target.value,
                        },
                      })
                    }
                    className="w-full bg-gray-50/50"
                    disabled={updateCompanyMutation.isPending}
                    placeholder="e.g. 395006"
                  />
                </div>
                <div>
                  <label
                    htmlFor={`${profileFieldId}-country`}
                    className="text-sm text-text-muted block mb-1"
                  >
                    Country Code (2-Letter)
                  </label>
                  <DashboardInput
                    id={`${profileFieldId}-country`}
                    type="text"
                    value={companyForm.address.country}
                    onChange={(e) =>
                      setCompanyForm({
                        ...companyForm,
                        address: {
                          ...companyForm.address,
                          country: e.target.value.toUpperCase().slice(0, 2),
                        },
                      })
                    }
                    className="w-full bg-gray-50/50"
                    disabled={updateCompanyMutation.isPending}
                    placeholder="e.g. IN"
                  />
                </div>
              </div>
            </div>
          </div>
        </SideDrawer>

        {/* Profile Photo Crop Modal */}
        <ImageCropModal
          isOpen={isCropModalOpen}
          imageSrc={selectedCropImage}
          onClose={handleCloseCropModal}
          onCropComplete={handleCropComplete}
          cropShape="round"
          aspectRatio={1}
          title="Crop Profile Photo"
        />
      </div>
    </DashboardPage>
  );
}
